import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import useAdd from "../../../../../api/useAdd";
import { useAuth } from "../../../../../context";
import useFetchObjects from "../../../../../api/useFetchObjects";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const EmployeeAdd = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const [positionData, setPositionData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);

  const { data: positions, isSuccess: positionSuccess } = useFetchObjects(
    ["position"],
    "position",
    token
  );

  const { data: departments, isSuccess: departmentSuccess } = useFetchObjects(
    ["departments"],
    "departments",
    token
  );

  useEffect(() => {
    if (positionSuccess) {
      setPositionData(positions);
    }
  }, [positionSuccess, positions]);

  useEffect(() => {
    if (departmentSuccess) {
      setDepartmentData(departments);
    }
  }, [departmentSuccess, departments]);

  const { handleAdd, resetForm, loading } = useAdd("users", token);

  useEffect(() => {
    if (resetForm) {
      navigate(-1);
    }
  }, [resetForm]);

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phone: "",
    first_name: "",
    last_name: "",
    salary_amount: "",
    contract_date: "",
    available_contract_date: "",
    department: "",
    position: "",
    password: "",
    confirm_password: "",
    is_manager_in_call_center: "",
    call_center_admin: "",
    image: null,
    cv: null,
    educational_document: null,
    is_exist: true,
  });

  const [errors, setErrors] = useState({});
  const [permissions, setPermissions] = useState({
    reception: false,
    pharmacy: false,
    doctor: false,
    nurse: false,
    x_ray: false,
    ultrasounds: false,
    laboratory: false,
    echo: false,
    finance: false,
    reports: false,
    hr: false,
    academic: false,
    dental: false,
    call_center: false,
    beauty: false,
    htPatient: false,
    operations: false,
    emergency: false,
    bedManagement: false,
    ipd: false,
    icu: false,
    night_shift: false,
    night_shift_manage: false,
  });

  const [limitedDataView, setLimitedDataView] = useState(false);

  const validate = () => {
    let tempErrors = {};
    if (!formData.username) tempErrors.username = t("hr.usernameRequired");
    if (!formData.email) tempErrors.email = t("hr.emailRequired");
    if (!formData.phone) tempErrors.phone = t("hr.phoneNumberRequired");
    if (!formData.first_name) tempErrors.first_name = t("hr.firstNameRequired");
    if (!formData.last_name) tempErrors.last_name = t("hr.lastNameRequired");
    if (!formData.salary_amount)
      tempErrors.salary_amount = t("hr.salaryAmountRequired");
    if (!formData.contract_date)
      tempErrors.contract_date = t("hr.contractDateRequired");
    if (!formData.available_contract_date)
      tempErrors.available_contract_date = t(
        "hr.availableContractDateRequired"
      );
    if (!formData.password) tempErrors.password = t("hr.passwordRequired");
    if (!formData.confirm_password)
      tempErrors.confirm_password = t("hr.passwordConfirmRequired");
    if (formData.password && formData.password !== formData.confirm_password) {
      tempErrors.confirm_password = t("hr.passwordNotMatch");
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;

    if (files && files[0]) {
      const file = files[0];

      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, [name]: "File size must be less than 5MB" });
        return;
      }
      setErrors({ ...errors, [name]: null });
      setFormData({ ...formData, [name]: file });
    }
  };

  const handlePermissionChange = (event) => {
    setPermissions({
      ...permissions,
      [event.target.name]: event.target.checked,
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validate()) {
      const data = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== "") {
          data.append(key, formData[key]);
        }
      });
      Object.keys(permissions).forEach((key) => {
        data.append(key, permissions[key]);
      });
      data.append("limited_data_view", limitedDataView);
      handleAdd(data);
    }
  };

  return (
    <Container>
      <Box sx={{ paddingY: 4, maxWidth: "800px", marginX: "auto" }}>
        <Typography variant="h5" fontWeight={600} align="center" gutterBottom>
          {t("hr.addNewEmployee")}
        </Typography>

        <Box sx={{ marginTop: 3 }}>
          <Typography fontWeight={500} gutterBottom>
            {t("hr.personalInformation")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.username")}
                name="username"
                value={formData.username}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.username}
                helperText={errors.username}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.email")}
                name="email"
                value={formData.email}
                onChange={handleChange}
                type="email"
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.firstName")}
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.first_name}
                helperText={errors.first_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.lastName")}
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.last_name}
                helperText={errors.last_name}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("hr.phone")}
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                type="number"
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ marginTop: 4 }}>
          <Typography fontWeight={500} gutterBottom>
            {t("hr.officeInformation")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.salaryAmount")}
                name="salary_amount"
                value={formData.salary_amount}
                onChange={handleChange}
                type="number"
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.salary_amount}
                helperText={errors.salary_amount}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.contractDate")}
                name="contract_date"
                value={formData.contract_date}
                onChange={handleChange}
                type="date"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.contract_date}
                helperText={errors.contract_date}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.availableContractDate")}
                name="available_contract_date"
                value={formData.available_contract_date}
                onChange={handleChange}
                type="date"
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.available_contract_date}
                helperText={errors.available_contract_date}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={t("hr.position")}
                name="position"
                value={formData.position}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                margin="dense"
              >
                {positionData.map((item, index) => (
                  <MenuItem value={item.id} key={index}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label={t("hr.department")}
                name="department"
                value={formData.department}
                onChange={handleChange}
                variant="outlined"
                fullWidth
                margin="dense"
              >
                {departmentData.map((item, index) => (
                  <MenuItem value={item.id} key={index}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ marginTop: 4 }}>
          <Typography fontWeight={500} gutterBottom>
            {t("hr.passwordInformation")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.password")}
                name="password"
                value={formData.password}
                onChange={handleChange}
                type="password"
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.password}
                helperText={errors.password}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("hr.confirmPassword")}
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                type="password"
                variant="outlined"
                fullWidth
                margin="dense"
                error={!!errors.confirm_password}
                helperText={errors.confirm_password}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ marginTop: 4 }}>
          <Typography fontWeight={500} gutterBottom>
            {t("hr.documents")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                margin="dense"
              >
                {t("hr.uploadImage")}
                <input
                  type="file"
                  name="image"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
              {formData.image && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {formData.image.name}
                </Typography>
              )}
              {errors.image && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {errors.image}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                margin="dense"
              >
                {t("hr.uploadCV")}
                <input
                  type="file"
                  name="cv"
                  hidden
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                />
              </Button>
              {formData.cv && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {formData.cv.name}
                </Typography>
              )}
              {errors.cv && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {errors.cv}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                margin="dense"
              >
                {t("hr.uploadEducationalDocument")}
                <input
                  type="file"
                  name="educational_document"
                  hidden
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                />
              </Button>
              {formData.educational_document && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {formData.educational_document.name}
                </Typography>
              )}
              {errors.educational_document && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {errors.educational_document}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ marginTop: 4 }}>
          <Typography fontWeight={500} gutterBottom>
            {t("hr.permissions")}
          </Typography>
          <Grid container spacing={2}>
            {Object.keys(permissions)
              .filter(
                (p) => !["call_center", "beauty", "htPatient"].includes(p)
              )
              .map((permission) => (
              <Grid item xs={6} sm={4} md={3} key={permission}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={permissions[permission]}
                      onChange={handlePermissionChange}
                      name={permission}
                    />
                  }
                  label={t(`hr.${permission}`)}
                />
              </Grid>
            ))}
            {/* Volunteer option commented out
            <Grid item xs={6} sm={4} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={limitedDataView}
                    onChange={(e) => setLimitedDataView(e.target.checked)}
                    name="limitedDataView"
                  />
                }
                label={t("hr.limitedDataView")}
              />
            </Grid>
            */}
            {/* Call Center permission options commented out - not needed for now
            {permissions?.call_center && (
              <>
                {" "}
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="is_manager_in_call_center"
                        checked={formData.is_manager_in_call_center}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            is_manager_in_call_center: e.target.checked,
                          });
                        }}
                      />
                    }
                    label={t("hr.managerCallCenter")}
                  />
                </Grid>{" "}
                <Grid item xs={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="call_center_admin"
                        checked={formData.call_center_admin}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            call_center_admin: e.target.checked,
                          });
                        }}
                      />
                    }
                    label={t("hr.callCenterAdmin", "Call Center Admin")}
                  />
                </Grid>
              </>
            )}
            */}
          </Grid>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            startIcon={loading && <CircularProgress size={20} />}
            disabled={loading}
          >
            {t("hr.addEmployee")}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default EmployeeAdd;
